import { Autocomplete, AutocompleteProps, CircularProgress, TextField } from "@mui/material";
import { InputError } from "./types";
import i18n from "i18next";
import { AnyError } from "src/errorHandling/anyError";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/userErrorDialog.ts";
import { useDebounce } from "src/utils/useDebounce.ts";
import { CustomPopper } from "src/components/common/popper/CustomPopper.tsx";
import { useMemo, useState } from "react";
import { useAsyncFetch } from "src/utils/async/asyncFetch.ts";

export interface AsyncSelectFieldProps<T, Key extends string | number>
	extends Omit<
		AutocompleteProps<T, false, false, false>,
		| "onChange"
		| "options"
		| "renderInput"
		| "getOptionKey"
		| "getOptionLabel"
		| "freeSolo"
		| "multiple"
		| "filterOptions"
		| "isOptionEqualToValue"
		| "value"
		| "disableClearable"
	> {
	fetchOptions: AsyncSelectFieldFetchOptionsFunc<Key, T>;
	onChange: (v: T | null) => void;
	value?: Key | null;
	getOptionKey: (o: T) => Key;
	getOptionLabel: (o: T) => string;
	label: string;
	error?: InputError;
	placeholder?: string;
	dropdownWidth?: number | string;
}

export type AsyncSelectFieldFetchOptionsFunc<Key, T> = (params: {
	searchQuery: string;
	currentSelection: Key | undefined;
}) => Promise<T[]>;

export const AsyncSelectField = <T, Key extends string | number>({
	label,
	value: valueKey,
	onChange,
	fetchOptions: fetchOptionsProp,
	autoFocus,
	error,
	placeholder,
	noOptionsText,
	getOptionKey,
	getOptionLabel,
	dropdownWidth,
	sx,
	...other
}: AsyncSelectFieldProps<T, Key>) => {
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [valueKeyInternal, setValueKeyInternal] = useState<Key | null | undefined>(valueKey);
	const [optionsAsync, fetchOptions] = useAsyncFetch<T[], string>(
		(newSearchQuery) =>
			fetchOptionsProp({
				searchQuery: newSearchQuery ?? searchQuery,
				currentSelection: valueKey ?? undefined,
			}),
		{
			fetchOnMount: valueKey != null,
		},
	);

	const showErrorDialog = useErrorDialog();
	const debounce = useDebounce();

	const onInputChanged = (newInput: string) => {
		setSearchQuery(newInput);
		debounce(200, async () => {
			try {
				await fetchOptions(newInput);
			} catch (e) {
				showErrorDialog(e as AnyError);
			}
		});
	};

	const value: T | undefined | null = useMemo(() => {
		if (valueKeyInternal === undefined) return undefined;
		if (valueKeyInternal == null) return null;
		if (optionsAsync.data === undefined) return null;

		const matchingOption = optionsAsync.data.find((o) => getOptionKey(o) === valueKeyInternal);
		if (matchingOption != null) return matchingOption;

		console.error(
			"AsyncSelectField: value not found in options",
			valueKeyInternal,
			optionsAsync.data,
		);
		return null;
	}, [optionsAsync.data, valueKeyInternal, getOptionKey]);

	return (
		<Autocomplete
			options={optionsAsync.data || []}
			filterOptions={(o) => o}
			value={value}
			getOptionKey={getOptionKey}
			getOptionLabel={getOptionLabel}
			disableClearable={false}
			onChange={(_, value) => {
				const newValueKey = value == null ? null : getOptionKey(value);
				setValueKeyInternal(newValueKey);
				onChange(value);
			}}
			isOptionEqualToValue={(option, value) => {
				if (value == null) return false;
				return getOptionKey(option) === getOptionKey(value);
			}}
			noOptionsText={noOptionsText !== undefined ? noOptionsText : i18n.t("no_options")}
			onInputChange={(_, newInput) => {
				onInputChanged(newInput);
			}}
			inputValue={searchQuery}
			onOpen={() => {
				setValueKeyInternal(null);
				onInputChanged("");
			}}
			onClose={(_, reason) => {
				if (reason != "selectOption") {
					setValueKeyInternal(valueKey);
				}
			}}
			PopperComponent={(popperProps) => (
				<CustomPopper {...popperProps} width={dropdownWidth} />
			)}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						label={label}
						error={error !== undefined}
						helperText={error}
						autoFocus={autoFocus}
						placeholder={placeholder || i18n.t("search_options")}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{optionsAsync.loading && (
										<CircularProgress size={20} color={"inherit"} />
									)}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				);
			}}
			sx={{
				minWidth: 150,
				...sx,
			}}
			{...other}
		/>
	);
};
